import { Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerClose, DrawerContent } from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { searchEntries } from "../matcher";
import type { SearchResult } from "../types";
import { useSearchEntries } from "../use-search-entries";

interface SearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Mobile fullscreen search. Built on vaul Drawer, which provides native-feeling
 * swipe-down dismissal. The X button gives an explicit close affordance for
 * users who prefer tapping.
 */
export function SearchModal({ open, onOpenChange }: SearchModalProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("settings");
  const [query, setQuery] = useState("");
  const entries = useSearchEntries();

  const results = useMemo(() => searchEntries(query, entries), [query, entries]);

  const handleResultClick = (result: SearchResult) => {
    const path = `/settings/${result.sectionId}`;
    navigate(result.anchor ? `${path}#${result.anchor}` : path);
    setQuery("");
    onOpenChange(false);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) setQuery("");
        onOpenChange(next);
      }}
    >
      <DrawerContent className="h-[92vh] mt-0 rounded-t-2xl">
        <div className="flex items-center gap-2 px-4 pb-2 border-b">
          <Search className="w-5 h-5 text-muted-foreground flex-shrink-0" />
          <Input
            autoFocus
            placeholder={t("search.placeholder")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="flex-1 border-none focus-visible:ring-0 focus-visible:ring-offset-0 px-0 shadow-none"
          />
          <DrawerClose asChild>
            <Button variant="ghost" size="icon" aria-label={t("search.close")}>
              <X className="w-5 h-5" />
            </Button>
          </DrawerClose>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {query.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {t("search.emptyHint")}
            </div>
          ) : results.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {t("search.noResults")}
            </div>
          ) : (
            <SearchResultsList results={results} onSelect={handleResultClick} />
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

interface SearchResultsListProps {
  results: SearchResult[];
  onSelect: (result: SearchResult) => void;
}

function SearchResultsList({ results, onSelect }: SearchResultsListProps) {
  const { t } = useTranslation("settings");
  const sections = results.filter((result) => result.kind === "section");
  const fields = results.filter((result) => result.kind === "field");

  return (
    <div className="space-y-4">
      {sections.length > 0 && (
        <ResultGroup label={t("search.groupSections")} results={sections} onSelect={onSelect} />
      )}
      {fields.length > 0 && (
        <ResultGroup label={t("search.groupFields")} results={fields} onSelect={onSelect} />
      )}
    </div>
  );
}

interface ResultGroupProps {
  label: string;
  results: SearchResult[];
  onSelect: (result: SearchResult) => void;
}

function ResultGroup({ label, results, onSelect }: ResultGroupProps) {
  return (
    <div className="space-y-1">
      <div className="px-3 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      {results.map((result, index) => (
        <button
          key={`${result.kind}-${result.sectionId}-${result.fieldId ?? index}`}
          type="button"
          onClick={() => onSelect(result)}
          className="w-full text-left p-3 rounded-lg hover:bg-muted active:bg-muted/80 transition-colors"
        >
          <div className="font-medium text-sm text-foreground">{result.label}</div>
          {result.breadcrumb ? (
            <div className="text-xs text-muted-foreground mt-0.5">{result.breadcrumb}</div>
          ) : null}
        </button>
      ))}
    </div>
  );
}
